import { eventNames, useCoreEvents } from '@carvana/core-events';
import { DispatcherEvent } from '@carvana/merch-native';
import { useCallback, useContext } from 'react';
import { ToastActions } from '@/common/FavoritesToastContent/types';
import useDisplayFavoritesToast from '@/common/FavoritesToastContent/useDisplayFavoritesToast';
import { useSendMessageToNative } from '~/context/common/NativeContext';
import UserContext, { useIsLoggedIn } from '~/context/common/UserContext';
import { removeFavorite } from '~/services/common';
import { logError } from '~/utilities/loggers';
import type { FavoritesArguments } from './types';

type LifeCycleFn = (args: FavoritesArguments) => void;

type Options = {
  beforeRemoveFavorite?: LifeCycleFn;
};

const dummyFn = () => {};

export default function useRemoveFavorite({
  beforeRemoveFavorite = dummyFn
}: Options = {}) {
  const {
    favorites: { setData, setLoading }
  } = useContext(UserContext);
  const isLoggedIn = useIsLoggedIn();
  const { emit } = useCoreEvents();
  const displayFavoritesToast = useDisplayFavoritesToast();
  const sendMessageToNative = useSendMessageToNative();

  return useCallback(
    async (args: FavoritesArguments) => {
      const { stockNumber, vehicleId } = args;

      if (!isLoggedIn) return;

      try {
        beforeRemoveFavorite(args);

        setLoading();

        const {
          data: { content: vehicles }
        } = await removeFavorite(vehicleId);

        sendMessageToNative(DispatcherEvent.FavoriteRemoved, {
          stockNumber: stockNumber ?? 0,
          vehicleId: vehicleId
        });

        setData(vehicles);

        emit(eventNames.favorites.mfe.refresh, { source: 'app', vehicles });
        displayFavoritesToast(ToastActions.remove);
      } catch (err) {
        logError(err, { location: 'Remove favorite' });
      }
    },
    [
      beforeRemoveFavorite,
      displayFavoritesToast,
      emit,
      isLoggedIn,
      sendMessageToNative,
      setData,
      setLoading
    ]
  );
}
